import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Button, Drawer, Popover } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';

import RecordBadge from '../common/RecordBadge';
import { Skeleton } from '../loading';
import { CHECK, COLLECTION_ICON, LAYERS } from '../icons';
import { switchTargetPath } from '../../config/collectionShell';
import useCollectionSwitcherSource from '../../hooks/useCollectionSwitcherSource';
import useCombinedListMatches from '../../hooks/useCombinedListMatches';
import useWindowClass from '../../hooks/useWindowClass';
import { combinedListPath } from '../../utils/combinedLists';
import { parentKicker } from '../../utils/collectionFamily';
import { normalizeId } from '../../utils/ids';

/** Case-insensitive match on the collection's name, or its parent's and name together. */
export function matchesCollection(collection, query) {
	const needle = String(query || '').trim().toLowerCase();
	if (!needle) return true;
	const parent = parentKicker(collection);
	const hay = `${parent ? `${parent} ` : ''}${collection?.name || ''}`.toLowerCase();
	return needle.split(/\s+/).every(word => hay.includes(word));
}

/**
 * The rows the panel draws and the arrow keys walk, in order. With nothing
 * typed: Recent, then Favorites, then the All collections link. With a query:
 * the matches among every collection the person can open (one list, so a
 * collection that is both recent and starred is not offered twice), then the link.
 * The person's saved combined lists (KPR-1022) follow as their own section,
 * narrowed by the same words: a list across collections is one jump away from
 * any of them.
 */
export function switcherSections({ source, query, lists = [] }) {
	const typed = String(query || '').trim();
	const sections = typed
		? [{ key: 'matches', label: 'Collections', collections: source.all.filter(collection => matchesCollection(collection, typed)) }]
		: [
			{ key: 'recent', label: 'Recent', collections: source.recent },
			{ key: 'favorites', label: 'Favorites', collections: source.favorites },
		];
	const savedLists = (lists || []).filter(list => list?._id && matchesCollection({ name: list.name }, typed));
	return [
		...sections.filter(section => section.collections.length),
		...(savedLists.length ? [{ key: 'lists', label: 'Combined lists', collections: [], lists: savedLists }] : []),
	];
}

function SwitcherPanel({ collectionId, onChoose, onClose, autoFocus = true }) {
	const source = useCollectionSwitcherSource();
	const { lists } = useCombinedListMatches();
	const listId = useId();
	const [query, setQuery] = useState('');
	const [active, setActive] = useState(0);
	const inputRef = useRef(null);

	const sections = useMemo(() => switcherSections({ source, query, lists }), [source, query, lists]);
	const options = useMemo(() => [
		...sections.flatMap(section => (section.lists || []).length
			? section.lists.map(list => ({ key: `${section.key}-${list._id}`, list, link: combinedListPath(list._id) }))
			: section.collections.map(collection => ({
				key: `${section.key}-${normalizeId(collection._id)}`,
				collection,
			}))),
		{ key: 'all-collections', link: '/collections' },
	], [sections]);

	useEffect(() => { setActive(0); }, [query, options.length]);
	// After the popup's own mount and motion, as the palette does: focusing a
	// node that is still being placed is dropped by the browser.
	useEffect(() => {
		if (!autoFocus) return undefined;
		const timer = setTimeout(() => inputRef.current?.focus(), 50);
		return () => clearTimeout(timer);
	}, [autoFocus]);

	const choose = option => {
		if (!option) return;
		if (option.link) onChoose({ to: option.link });
		else onChoose({ collection: option.collection });
	};

	const onKeyDown = event => {
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			setActive(index => (index + 1) % options.length);
		} else if (event.key === 'ArrowUp') {
			event.preventDefault();
			setActive(index => (index - 1 + options.length) % options.length);
		} else if (event.key === 'Home') {
			event.preventDefault();
			setActive(0);
		} else if (event.key === 'End') {
			event.preventDefault();
			setActive(options.length - 1);
		} else if (event.key === 'Enter') {
			event.preventDefault();
			choose(options[active]);
		} else if (event.key === 'Escape') {
			event.preventDefault();
			event.stopPropagation();
			onClose();
		}
	};

	const optionId = option => `${listId}-${option.key}`;
	const typed = Boolean(query.trim());
	const empty = !sections.length;
	let cursor = -1;

	return (
		<div className="collection-switcher" role="dialog" aria-label="Switch collection" data-shell-switcher>
			<input
				ref={inputRef}
				type="search"
				className="collection-switcher__search"
				placeholder="Search collections"
				aria-label="Search collections"
				role="combobox"
				aria-expanded="true"
				aria-controls={listId}
				aria-autocomplete="list"
				aria-activedescendant={options[active] ? optionId(options[active]) : undefined}
				autoComplete="off"
				value={query}
				onChange={event => setQuery(event.target.value)}
				onKeyDown={onKeyDown}
			/>
			<ul className="collection-switcher__list" id={listId} role="listbox" aria-label="Collections">
				{sections.map(section => (
					<React.Fragment key={section.key}>
						<li role="presentation" className="collection-switcher__heading">{section.label}</li>
						{(section.lists || []).map(list => {
							cursor += 1;
							const index = cursor;
							const option = options[index];
							return (
								<li
									key={option.key}
									id={optionId(option)}
									role="option"
									aria-selected={index === active}
									data-active={index === active ? 'true' : undefined}
									className="collection-switcher__option"
									onMouseMove={() => { if (active !== index) setActive(index); }}
									onClick={() => choose(option)}
								>
									<RecordBadge
										record={{ _id: list._id, name: list.name, icon: null, color: null }}
										kind="card"
										kicker="Combined list"
										fallbackIcon={LAYERS}
										title={list.name || 'Combined list'}
										size="sm"
									/>
								</li>
							);
						})}
						{section.collections.map(collection => {
							cursor += 1;
							const index = cursor;
							const option = options[index];
							const id = normalizeId(collection._id);
							const current = id === normalizeId(collectionId);
							return (
								<li
									key={option.key}
									id={optionId(option)}
									role="option"
									aria-selected={index === active}
									aria-current={current ? 'true' : undefined}
									data-current={current ? 'true' : undefined}
									data-active={index === active ? 'true' : undefined}
									className="collection-switcher__option"
									onMouseMove={() => { if (active !== index) setActive(index); }}
									onClick={() => choose(option)}
								>
									<RecordBadge record={collection} kind="collection" title={collection.name || 'Untitled collection'} size="sm" />
									{current ? (
										<span className="collection-switcher__current">
											<CHECK aria-hidden="true" />
											<span>Current</span>
										</span>
									) : null}
								</li>
							);
						})}
					</React.Fragment>
				))}
				{source.error ? (
					<li role="presentation" className="collection-switcher__empty">
						<span role="alert">Unable to load collections.</span>{' '}
						<Button size="small" onClick={source.retry}>Retry</Button>
					</li>
				) : null}
				{empty && !source.error ? (
					<li role="presentation" className="collection-switcher__empty">
						{source.loading ? (
							<>
								<span className="loading-sr-only">Loading collections</span>
								<Skeleton variant="list-row" count={3} />
							</>
						) : typed ? 'No collection matches. Try another name.' : 'Open a collection and it shows up here. Search to find any other.'}
					</li>
				) : null}
				<li
					id={optionId(options[options.length - 1])}
					role="option"
					aria-selected={active === options.length - 1}
					data-active={active === options.length - 1 ? 'true' : undefined}
					className="collection-switcher__option collection-switcher__all"
					onMouseMove={() => { if (active !== options.length - 1) setActive(options.length - 1); }}
					onClick={() => choose(options[options.length - 1])}
				>
					<COLLECTION_ICON aria-hidden="true" />
					<span>All collections</span>
				</li>
			</ul>
		</div>
	);
}

/**
 * THE COLLECTION SWITCHER (docs/platform-standards.md §13.2; KPR-640): the
 * crumb's collection node jumps to another collection without going back to All
 * collections. A search box, the collections opened lately, the ones starred, an
 * All collections link; only collections the person can open; the current one
 * marked. A choice lands on the SAME section of the other collection when it has
 * one (List to List), otherwise on its Home.
 *
 * Open state belongs to the caller (the crumb), so the "…" menu a phone folds
 * the path into can open it too. A popover under the node on a wide screen; a
 * full-width sheet from the bottom on a phone. The panel mounts only while open,
 * so a page that never opens the switcher asks the server for nothing. Escape
 * closes it and hands focus back to `triggerRef`.
 */
export default function CollectionSwitcher({ collectionId, open, onOpenChange, triggerRef, trigger = null }) {
	// compact (§18): a sheet from the bottom; medium and expanded: a popover.
	const { compact: phone } = useWindowClass();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const close = (returnFocus = true) => {
		onOpenChange(false);
		if (returnFocus) setTimeout(() => triggerRef?.current?.focus(), 0);
	};

	const onChoose = ({ collection, to }) => {
		close();
		if (to) { navigate(to); return; }
		if (normalizeId(collection._id) === normalizeId(collectionId)) return;
		navigate(switchTargetPath(pathname, normalizeId(collectionId), { ...collection, _id: normalizeId(collection._id) }));
	};

	const panel = <SwitcherPanel collectionId={collectionId} onChoose={onChoose} onClose={() => close()} />;

	if (phone) {
		return (
			<>
				{trigger ? React.cloneElement(trigger, { onClick: () => onOpenChange(!open) }) : null}
				<Drawer
					open={open}
					onClose={() => close()}
					placement="bottom"
					height="min(80vh, 560px)"
					title="Switch collection"
					className="collection-switcher-sheet"
					destroyOnClose
				>
					{open ? panel : null}
				</Drawer>
			</>
		);
	}

	return (
		<Popover
			open={open}
			onOpenChange={next => { if (!next) close(false); else onOpenChange(true); }}
			trigger="click"
			placement="bottomLeft"
			arrow={false}
			overlayClassName="collection-switcher-popover"
			// Never empty: antd keeps a popover with no content shut.
			content={open ? panel : <span />}
		>
			{trigger}
		</Popover>
	);
}
